// apps/web/src/pages/estoque/nova-movimentacao-sheet.tsx — Fase 13, Plano 11 (EST-09, EST-14, EST-16, D-06, D-07, D-09).
// FormDialog "Nova movimentação" do estoque PRÓPRIO: transferir entre localizações, registrar perda/sucata e entregar consumível
// ao setor. Validação e montagem do payload ficam em lib/estoque/movimentacao.ts (puras); o saldo mostrado é só ajuda,
// quem decide é o servidor (409 SALDO_INSUFICIENTE mantém a FormDialog aberta com os dados digitados). Material via lookup de
// estoque (estoque:consultar) — nunca depende de pv:ler.
// Plano 14 (EST-09, D-22): em "Transferir", o item pode ser um LOTE de cliente. Aí a transferência é do lote inteiro
// (quantidade somente leitura), com motivo obrigatório, via POST /estoque/lotes/{loteId}/transferencias; as mudanças
// automáticas do processo não passam por aqui.
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Loader2 } from 'lucide-react';
import { MOTIVOS_PERDA } from '@pieltec/contracts';
import type {
  ListaSaldosProprios,
  LocalizacaoLookupEstoque,
  LoteLookupEstoque,
  MaterialLookupPv,
  MotivoPerda,
  SaldoProprioLinha,
  TransferirLoteInput,
} from '@pieltec/contracts';
import { CelulaSelect } from '../../components/estoque/celula-select';
import { LocalizacaoSelect } from '../../components/estoque/localizacao-select';
import { LoteCombobox } from '../../components/estoque/lote-combobox';
import { Button } from '../../components/ui/button';
import { FormMessage } from '../../components/ui/form-message';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { FormDialog, FormDialogContent, FormDialogDescription, FormDialogFooter, FormDialogHeader, FormDialogTitle } from '../../components/ui/form-dialog';
import { Textarea } from '../../components/ui/textarea';
import { MaterialCombobox } from '../../components/vendas/material-combobox';
import { apiMutate } from '../../lib/csrf-fetch';
import {
  LIMITE_TEXTO_MOVIMENTACAO,
  MENSAGEM_FALHA_MOVIMENTACAO,
  ROTULOS_TIPO_MOVIMENTACAO,
  TIPOS_MOVIMENTACAO,
  ajudaSaldoDisponivel,
  camposVazios,
  comChaveDaTentativa,
  montarPayload,
  saldoNoLocal,
  saldoPreferencial,
  tentativaDaMovimentacao,
  validarMovimentacao,
  type CamposMovimentacao,
  type CampoMovimentacao,
  type ResultadoValidacaoMovimentacao,
  type TentativaMovimentacao,
  type TipoMovimentacao,
} from '../../lib/estoque/movimentacao';
import { formatarQuantidadeEstoque } from '../../lib/estoque/formatacao';
import { filtrosSaldosVazios, montarQuerySaldosProprios } from '../../lib/estoque/saldos';
import { validarTransferenciaLote } from '../../lib/estoque/saldos-clientes';
import { lerErroApi } from '../../lib/vendas/erro-api';

const ENDPOINT_MATERIAIS_ESTOQUE = '/api/v1/estoque/lookups/materiais';
/** Rótulos do select "Motivo da perda" (UI-SPEC), um por motivo do contrato. */
const ROTULOS_MOTIVO_PERDA: Readonly<Record<MotivoPerda, string>> = {
  VENCIDA: 'Vencida',
  CONTAMINADA: 'Contaminada',
  DERRAMAMENTO: 'Derramamento',
  BORRA: 'Borra',
  OUTRO: 'Outro',
};
/** Transferência manual de lote de cliente (a de matéria-prima usa ROTAS_MOVIMENTACAO: .../movimentos-proprios/transferencias). */
function rotaTransferenciaLote(loteId: string): string {
  return `/api/v1/estoque/lotes/${encodeURIComponent(loteId)}/transferencias`;
}

type AlvoTransferencia = 'MATERIA_PRIMA' | 'LOTE';
const ROTULOS_ALVO: Readonly<Record<AlvoTransferencia, string>> = { MATERIA_PRIMA: 'Matéria-prima', LOTE: 'Lote de cliente' };
const ALVOS_TRANSFERENCIA: readonly AlvoTransferencia[] = ['MATERIA_PRIMA', 'LOTE'];

const AJUDA_MOTIVO_LOTE = 'Lotes mudam de local sozinhos pelo processo. Use a transferência manual só para exceções.';
const APOIO_CONSUMIVEL = 'Baixado na entrega ao setor, sem OP e sem devolução. O custo fica atribuído ao setor.';

/** Material escolhido: o lookup de estoque devolve a unidade-base além de id, código e nome. */
type MaterialEscolhido = MaterialLookupPv & { unidade: string };

/** Pré-preenchimento vindo da tela de Saldos (menu "⋯" e linha expandida). */
export interface InicialMovimentacao {
  readonly tipo: TipoMovimentacao;
  readonly materialId: string;
  readonly codigo: string;
  readonly nome: string;
  readonly unidade: string;
  readonly localizacaoId: string;
}

/** Lote de cliente já escolhido na aba "Material de clientes" (menu "⋯" e linha expandida): abre em Transferir. */
export interface InicialMovimentacaoLote {
  readonly lote: LoteLookupEstoque;
  /** Unidade do saldo do lote (o lookup de lotes não a traz). */
  readonly unidade: string;
}

export interface NovaMovimentacaoSheetProps {
  readonly aberto: boolean;
  readonly aoFechar: () => void;
  /** Chamado após cada movimentação registrada, para a tela recarregar a lista. */
  readonly aoRegistrar: () => void;
  readonly inicial?: InicialMovimentacao | null;
  readonly inicialLote?: InicialMovimentacaoLote | null;
}

type ErrosMovimentacao = Partial<Record<CampoMovimentacao, string>>;

/** Erros sem os campos já corrigidos (o erro some assim que o usuário mexe no campo). */
function semCampos(erros: ErrosMovimentacao, chaves: readonly string[]): ErrosMovimentacao {
  return Object.fromEntries(Object.entries(erros).filter(([chave]) => !chaves.includes(chave)));
}

interface CampoProps {
  readonly campo: CampoMovimentacao | 'referencia';
  readonly rotulo: string;
  readonly erro?: string;
  readonly ajuda?: string | null;
  readonly children: React.ReactNode;
}

function Campo({ campo, rotulo, erro, ajuda, children }: CampoProps) {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={`mov-${campo}`}>{rotulo}</Label>
      {children}
      {ajuda && !erro && <p className="text-xs text-muted-foreground">{ajuda}</p>}
      {erro && <FormMessage>{erro}</FormMessage>}
    </div>
  );
}

export function NovaMovimentacaoSheet({ aberto, aoFechar, aoRegistrar, inicial, inicialLote }: NovaMovimentacaoSheetProps) {
  const [tipo, setTipo] = useState<TipoMovimentacao>('TRANSFERENCIA');
  const [alvo, setAlvo] = useState<AlvoTransferencia>('MATERIA_PRIMA');
  const [lote, setLote] = useState<LoteLookupEstoque | null>(null);
  const [unidadeLote, setUnidadeLote] = useState('');
  const [material, setMaterial] = useState<MaterialEscolhido | null>(null);
  const [campos, setCampos] = useState<CamposMovimentacao>(camposVazios);
  const [erros, setErros] = useState<ErrosMovimentacao>({});
  const [linhasSaldo, setLinhasSaldo] = useState<readonly SaldoProprioLinha[] | null>(null);
  const [recargaSaldo, setRecargaSaldo] = useState(0);
  const [nomesLocais, setNomesLocais] = useState<Readonly<Record<string, string>>>({});
  const [erroApi, setErroApi] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const enviandoRef = useRef(false);
  // WR-C01: chave de idempotência da tentativa atual (mantida no reenvio do mesmo conteúdo, renovada quando o payload muda).
  const tentativaRef = useRef<TentativaMovimentacao | null>(null);

  // Abre sempre limpo; com `inicial` já vem com item e local preenchidos.
  useEffect(() => {
    if (!aberto) return;
    setErros({});
    setErroApi(null);
    setSucesso(false);
    setLinhasSaldo(null);
    setAlvo('MATERIA_PRIMA');
    setLote(null);
    setUnidadeLote('');
    if (inicialLote) {
      setTipo('TRANSFERENCIA');
      setAlvo('LOTE');
      setLote(inicialLote.lote);
      setUnidadeLote(inicialLote.unidade);
      setMaterial(null);
      setCampos(camposVazios());
    } else if (inicial) {
      setTipo(inicial.tipo);
      setMaterial({ id: inicial.materialId, codigo: inicial.codigo, nome: inicial.nome, unidade: inicial.unidade });
      setCampos({
        ...camposVazios(),
        de: inicial.tipo === 'TRANSFERENCIA' ? inicial.localizacaoId : '',
        localizacaoId: inicial.tipo === 'PERDA' ? inicial.localizacaoId : '',
      });
    } else {
      setTipo('TRANSFERENCIA');
      setMaterial(null);
      setCampos(camposVazios());
    }
  }, [aberto, inicial, inicialLote]);

  // Saldo do material por localização (ajuda do campo Quantidade); sem leitura o servidor segue sendo quem valida.
  const materialId = material?.id ?? '';
  // WR-C05: ao trocar de material o saldo do anterior sai na hora, para a validação e a ajuda nunca usarem o saldo do outro item
  // enquanto a consulta nova não responde (a recarga após um registro mantém o valor antigo até chegar o novo).
  useEffect(() => {
    setLinhasSaldo(null);
  }, [materialId]);
  useEffect(() => {
    if (!aberto || materialId === '') {
      setLinhasSaldo(null);
      return;
    }
    let vivo = true;
    const query = montarQuerySaldosProprios(filtrosSaldosVazios(), { materialId, tamanhoPagina: 100 });
    fetch(`/api/v1/estoque/saldos/proprio?${query}`, { credentials: 'include' })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<ListaSaldosProprios>;
      })
      .then((json) => {
        if (vivo) setLinhasSaldo(json.itens);
      })
      .catch(() => {
        if (vivo) setLinhasSaldo(null);
      });
    return () => {
      vivo = false;
    };
  }, [aberto, materialId, recargaSaldo]);

  const lembrarNomes = useCallback((locais: readonly LocalizacaoLookupEstoque[]) => {
    setNomesLocais((atual) => ({ ...atual, ...Object.fromEntries(locais.map((local) => [local.id, local.nome])) }));
  }, []);

  function alterar(parcial: Partial<CamposMovimentacao>): void {
    setCampos((atual) => ({ ...atual, ...parcial }));
    setErros((atual) => semCampos(atual, Object.keys(parcial)));
    setErroApi(null);
    setSucesso(false);
  }

  function escolherMaterial(escolhido: MaterialEscolhido): void {
    setMaterial(escolhido);
    setErros((atual) => semCampos(atual, ['materialId']));
    setErroApi(null);
    setSucesso(false);
  }

  function escolherLote(escolhido: LoteLookupEstoque): void {
    setLote(escolhido);
    setErros((atual) => semCampos(atual, ['materialId']));
    setErroApi(null);
    setSucesso(false);
  }

  function trocarAlvo(proximo: AlvoTransferencia): void {
    setAlvo(proximo);
    setErros({});
    setErroApi(null);
    setSucesso(false);
  }

  const modoLote = tipo === 'TRANSFERENCIA' && alvo === 'LOTE';
  const unidade = material?.unidade ?? '';
  const linhaPreferencial = linhasSaldo?.find((linha) => linha.preferencial);
  const saldoDoTipo =
    tipo === 'TRANSFERENCIA'
      ? saldoNoLocal(linhasSaldo, campos.de)
      : tipo === 'PERDA'
        ? saldoNoLocal(linhasSaldo, campos.localizacaoId)
        : saldoPreferencial(linhasSaldo);
  const completos: CamposMovimentacao = { ...campos, materialId, unidade, saldo: saldoDoTipo };

  /** Lote, destino e motivo (obrigatório). O local atual vem só pelo nome no lookup: comparado pelo nome com os locais carregados. */
  function validarLote(): ResultadoValidacaoMovimentacao {
    const erros: ErrosMovimentacao = {};
    if (!lote) erros.materialId = 'Escolha o lote.';
    const atualId = lote ? (Object.entries(nomesLocais).find(([, nome]) => nome === lote.localizacaoNome)?.[0] ?? '') : '';
    const regras = validarTransferenciaLote({ para: campos.para, atual: atualId, motivo: campos.motivo });
    if (regras.para) erros.para = regras.para;
    if (regras.motivo) erros.motivo = regras.motivo;
    const primeiroInvalido = (['materialId', 'para', 'motivo'] as const).find((campo) => erros[campo] !== undefined) ?? null;
    return { erros, primeiroInvalido };
  }

  async function registrar(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (enviandoRef.current) return;
    const validacao = modoLote ? validarLote() : validarMovimentacao(tipo, completos);
    setErros(validacao.erros);
    setErroApi(null);
    setSucesso(false);
    if (validacao.primeiroInvalido) {
      document.getElementById(`mov-${validacao.primeiroInvalido}`)?.focus();
      return;
    }
    enviandoRef.current = true;
    setEnviando(true);
    try {
      let payload: { readonly rota: string; readonly corpo: object };
      if (modoLote && lote) {
        // A transferência de lote inteiro ainda não aceita chave de idempotência no servidor.
        payload = { rota: rotaTransferenciaLote(lote.id), corpo: { paraLocalizacaoId: campos.para, motivo: campos.motivo.trim() } satisfies TransferirLoteInput };
      } else {
        const base = montarPayload(tipo, completos);
        tentativaRef.current = tentativaDaMovimentacao(tentativaRef.current, base, () => crypto.randomUUID());
        payload = comChaveDaTentativa(base, tentativaRef.current);
      }
      const res = await apiMutate(payload.rota, { body: payload.corpo });
      if (!res.ok) {
        const erro = await lerErroApi(res, MENSAGEM_FALHA_MOVIMENTACAO);
        setErroApi(erro.mensagem);
        // Saldo mudou por baixo: a ajuda do campo passa a mostrar o valor atual.
        if (erro.status === 409) setRecargaSaldo((n) => n + 1);
        return;
      }
      setSucesso(true);
      tentativaRef.current = null;
      setCampos((atual) => ({ ...atual, quantidade: '', motivo: '', para: '' }));
      // O lote já mudou de local: a escolha é limpa para não reaproveitar o "De" antigo.
      if (modoLote) setLote(null);
      setRecargaSaldo((n) => n + 1);
      aoRegistrar();
    } catch {
      // Com chave de idempotência o reenvio é seguro; a transferência de lote ainda não tem chave, então o resultado é incerto.
      setErroApi(
        modoLote
          ? 'Não foi possível confirmar a transferência do lote. Confira o histórico do lote antes de tentar de novo.'
          : 'Não foi possível registrar a movimentação. Verifique a conexão e tente novamente: o reenvio não duplica o registro.',
      );
    } finally {
      enviandoRef.current = false;
      setEnviando(false);
    }
  }

  const idLocalDoSaldo = tipo === 'TRANSFERENCIA' ? campos.de : tipo === 'PERDA' ? campos.localizacaoId : (linhaPreferencial?.localizacaoId ?? '');
  const nomeLocalDoSaldo =
    nomesLocais[idLocalDoSaldo] ?? linhasSaldo?.find((linha) => linha.localizacaoId === idLocalDoSaldo)?.localizacaoNome ?? 'este local';
  const ajudaQuantidade = ajudaSaldoDisponivel(nomeLocalDoSaldo, saldoDoTipo, unidade);

  const campoQuantidade = (
    <Campo campo="quantidade" rotulo="Quantidade" erro={erros.quantidade} ajuda={ajudaQuantidade}>
      <Input
        id="mov-quantidade"
        inputMode="decimal"
        autoComplete="off"
        value={campos.quantidade}
        onChange={(e) => alterar({ quantidade: e.target.value })}
        aria-invalid={erros.quantidade !== undefined}
      />
    </Campo>
  );

  return (
    <FormDialog open={aberto} onOpenChange={(proximo) => (proximo ? undefined : aoFechar())}>
      <FormDialogContent>
        <form onSubmit={(e) => void registrar(e)} className="flex h-full flex-col gap-4" noValidate>
          <FormDialogHeader>
            <FormDialogTitle>Nova movimentação</FormDialogTitle>
            <FormDialogDescription>Movimentos manuais do estoque próprio. O histórico não é editado; correções são feitas por estorno.</FormDialogDescription>
          </FormDialogHeader>

          <div className="flex flex-col gap-4 px-4">
            <div role="status">{sucesso && <p className="text-sm font-semibold text-emerald-700">Movimentação registrada.</p>}</div>

            <RadioGroup
              value={tipo}
              onValueChange={(valor) => {
                setTipo(valor as TipoMovimentacao);
                // O local já escolhido acompanha a troca entre transferir e registrar perda.
                setCampos((atual) => ({ ...atual, de: atual.de || atual.localizacaoId, localizacaoId: atual.localizacaoId || atual.de }));
                setErros({});
                setErroApi(null);
                setSucesso(false);
              }}
              aria-label="Tipo de movimentação"
            >
              {TIPOS_MOVIMENTACAO.map((opcao) => (
                <div key={opcao} className="flex min-h-9 items-center gap-2">
                  <RadioGroupItem value={opcao} id={`mov-tipo-${opcao}`} />
                  <Label htmlFor={`mov-tipo-${opcao}`}>{ROTULOS_TIPO_MOVIMENTACAO[opcao]}</Label>
                </div>
              ))}
            </RadioGroup>

            {tipo === 'TRANSFERENCIA' && (
              <RadioGroup
                value={alvo}
                onValueChange={(valor) => trocarAlvo(valor === 'LOTE' ? 'LOTE' : 'MATERIA_PRIMA')}
                aria-label="O que transferir"
                className="flex flex-row gap-6"
              >
                {ALVOS_TRANSFERENCIA.map((opcao) => (
                  <div key={opcao} className="flex min-h-9 items-center gap-2">
                    <RadioGroupItem value={opcao} id={`mov-alvo-${opcao}`} />
                    <Label htmlFor={`mov-alvo-${opcao}`}>{ROTULOS_ALVO[opcao]}</Label>
                  </div>
                ))}
              </RadioGroup>
            )}

            <Campo campo="materialId" rotulo={tipo === 'CONSUMIVEL' ? 'Material' : 'Item'} erro={erros.materialId}>
              {modoLote ? (
                <LoteCombobox id="mov-materialId" value={lote} onChange={escolherLote} invalido={erros.materialId !== undefined} />
              ) : (
                <MaterialCombobox<MaterialEscolhido>
                  id="mov-materialId"
                  endpoint={ENDPOINT_MATERIAIS_ESTOQUE}
                  value={material}
                  onChange={escolherMaterial}
                  invalido={erros.materialId !== undefined}
                />
              )}
            </Campo>

            {modoLote && (
              <>
                <Campo campo="de" rotulo="De">
                  <Input id="mov-de" readOnly value={lote?.localizacaoNome ?? ''} placeholder="Escolha o lote" />
                </Campo>
                <Campo campo="quantidade" rotulo="Quantidade" ajuda="O lote é transferido inteiro, com todo o seu saldo.">
                  <Input id="mov-quantidade" readOnly value={lote ? formatarQuantidadeEstoque(lote.saldo, unidadeLote) : ''} />
                </Campo>
                <Campo campo="para" rotulo="Para" erro={erros.para}>
                  <LocalizacaoSelect id="mov-para" rotulo="Para" value={campos.para} onChange={(id) => alterar({ para: id })} aoCarregar={lembrarNomes} />
                </Campo>
                <Campo campo="motivo" rotulo="Motivo" erro={erros.motivo} ajuda={AJUDA_MOTIVO_LOTE}>
                  <Input
                    id="mov-motivo"
                    value={campos.motivo}
                    maxLength={LIMITE_TEXTO_MOVIMENTACAO}
                    onChange={(e) => alterar({ motivo: e.target.value })}
                    aria-invalid={erros.motivo !== undefined}
                    aria-required="true"
                  />
                </Campo>
              </>
            )}

            {tipo === 'TRANSFERENCIA' && !modoLote && (
              <>
                <Campo campo="de" rotulo="De" erro={erros.de}>
                  <LocalizacaoSelect id="mov-de" rotulo="De" value={campos.de} onChange={(id) => alterar({ de: id })} aoCarregar={lembrarNomes} />
                </Campo>
                {campoQuantidade}
                <Campo campo="para" rotulo="Para" erro={erros.para}>
                  <LocalizacaoSelect id="mov-para" rotulo="Para" value={campos.para} onChange={(id) => alterar({ para: id })} aoCarregar={lembrarNomes} />
                </Campo>
                <Campo campo="motivo" rotulo="Motivo (opcional)" erro={erros.motivo}>
                  <Input
                    id="mov-motivo"
                    value={campos.motivo}
                    maxLength={LIMITE_TEXTO_MOVIMENTACAO}
                    onChange={(e) => alterar({ motivo: e.target.value })}
                    aria-invalid={erros.motivo !== undefined}
                  />
                </Campo>
              </>
            )}

            {tipo === 'PERDA' && (
              <>
                <Campo campo="localizacaoId" rotulo="Localização" erro={erros.localizacaoId}>
                  <LocalizacaoSelect
                    id="mov-localizacaoId"
                    rotulo="Localização"
                    value={campos.localizacaoId}
                    onChange={(id) => alterar({ localizacaoId: id })}
                    aoCarregar={lembrarNomes}
                  />
                </Campo>
                {campoQuantidade}
                <Campo campo="motivoPerda" rotulo="Motivo da perda" erro={erros.motivoPerda}>
                  <Select value={campos.motivoPerda} onValueChange={(valor) => alterar({ motivoPerda: valor as MotivoPerda })}>
                    <SelectTrigger id="mov-motivoPerda" aria-invalid={erros.motivoPerda !== undefined} className="w-full">
                      <SelectValue placeholder="Selecione o motivo" />
                    </SelectTrigger>
                    <SelectContent>
                      {MOTIVOS_PERDA.map((motivo) => (
                        <SelectItem key={motivo} value={motivo}>
                          {ROTULOS_MOTIVO_PERDA[motivo]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Campo>
                <Campo campo="referencia" rotulo="Lote ou cor (opcional)">
                  <Input id="mov-referencia" value={campos.referencia} maxLength={80} onChange={(e) => alterar({ referencia: e.target.value })} />
                </Campo>
                <Campo
                  campo="detalhes"
                  rotulo="Detalhes"
                  erro={erros.detalhes}
                  ajuda={campos.motivoPerda === 'OUTRO' ? 'Obrigatório quando o motivo é Outro.' : null}
                >
                  <Textarea
                    id="mov-detalhes"
                    value={campos.detalhes}
                    maxLength={LIMITE_TEXTO_MOVIMENTACAO}
                    onChange={(e) => alterar({ detalhes: e.target.value })}
                    aria-invalid={erros.detalhes !== undefined}
                  />
                </Campo>
              </>
            )}

            {tipo === 'CONSUMIVEL' && (
              <>
                {campoQuantidade}
                <Campo campo="celulaId" rotulo="Setor / célula" erro={erros.celulaId}>
                  <CelulaSelect id="mov-celulaId" value={campos.celulaId} onChange={(id) => alterar({ celulaId: id })} invalido={erros.celulaId !== undefined} />
                </Campo>
                <Campo campo="observacao" rotulo="Observação (opcional)" erro={erros.observacao}>
                  <Input
                    id="mov-observacao"
                    value={campos.observacao}
                    maxLength={LIMITE_TEXTO_MOVIMENTACAO}
                    onChange={(e) => alterar({ observacao: e.target.value })}
                    aria-invalid={erros.observacao !== undefined}
                  />
                </Campo>
                <p className="text-sm text-muted-foreground">{APOIO_CONSUMIVEL}</p>
              </>
            )}

            {erroApi && <FormMessage>{erroApi}</FormMessage>}
          </div>

          <FormDialogFooter className="sticky bottom-0 border-t bg-background sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" className="min-h-11 sm:min-h-9" onClick={aoFechar}>
              Voltar
            </Button>
            <Button type="submit" className="min-h-11 sm:min-h-9" disabled={enviando}>
              {enviando && <Loader2 className="animate-spin" aria-hidden="true" />}
              Registrar movimentação
            </Button>
          </FormDialogFooter>
        </form>
      </FormDialogContent>
    </FormDialog>
  );
}
